import { useCallback, useEffect, useRef, useState } from "react";
import { customCoreInstance } from "../../api/CoreAxiosInstance";
import type { MyPayEnvironment } from "../../generated/core-api";
import type { BoundPayRun } from "../types/matching";
import { mockResolveByPayRunId } from "../utils/fixtures/mockPayRuns";

interface PayRunResolveResponse {
  payRunId: string;
  payGroupId: string;
  payGroupName: string;
  periodEnd: string;
  state: BoundPayRun["state"];
}

function readHttpError(error: unknown): {
  status: number | undefined;
  detail: string | undefined;
} {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return { status: undefined, detail: undefined };
  }
  const response = (error as { response?: { status?: number; data?: unknown } })
    .response;
  const status = response?.status;
  const data = response?.data;
  let detail: string | undefined;
  if (typeof data === "string" && data.trim()) {
    detail = data.trim();
  } else if (data && typeof data === "object" && "detail" in data) {
    const raw = (data as { detail: unknown }).detail;
    if (typeof raw === "string" && raw.trim()) detail = raw.trim();
  }
  return { status, detail };
}

/**
 * Option A — validate a pay run ID; derive pay group / period end / state.
 * Local Vite DEV may fall back to mock fixtures only when the API is
 * unreachable (no HTTP status). A real 4xx/5xx from Christmas/API must never
 * be masked as success — mockResolveByPayRunId accepts almost any id.
 */
export function usePayRunLookup(
  companyCode: string | null,
  mypayEnvironment: MyPayEnvironment | null
) {
  const [payRunIdInput, setPayRunIdInput] = useState("");
  const [resolvedPayRun, setResolvedPayRun] = useState<BoundPayRun | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  /** Bumps when company/environment changes so in-flight lookups are ignored. */
  const lookupGenerationRef = useRef(0);

  const clearResolved = useCallback(() => {
    setResolvedPayRun(null);
    setResolveError(null);
  }, []);

  // Company / environment change must not keep a prior company's resolved run.
  useEffect(() => {
    lookupGenerationRef.current += 1;
    setResolvedPayRun(null);
    setResolveError(null);
  }, [companyCode, mypayEnvironment]);

  const validatePayRunId = useCallback(
    async (rawId?: string): Promise<BoundPayRun | null> => {
      const payRunId = (rawId ?? payRunIdInput).trim();
      if (!companyCode || !mypayEnvironment || !payRunId) return null;
      const generation = lookupGenerationRef.current;
      setIsResolving(true);
      setResolveError(null);
      try {
        const res = await customCoreInstance<PayRunResolveResponse>({
          url: `/api/v1/onboarding/companies/${encodeURIComponent(companyCode)}/pay-runs/${encodeURIComponent(payRunId)}`,
          method: "GET",
          params: { mypay_environment: mypayEnvironment },
        });
        if (generation !== lookupGenerationRef.current) return null;
        setResolvedPayRun(res);
        setPayRunIdInput(res.payRunId);
        return res;
      } catch (error) {
        if (generation !== lookupGenerationRef.current) return null;
        const { status, detail } = readHttpError(error);

        // Offline DEV demo only — never override an actual API/Christmas status.
        if (import.meta.env.DEV && status === undefined) {
          const fallback = mockResolveByPayRunId(payRunId);
          if (fallback) {
            setResolvedPayRun(fallback);
            setPayRunIdInput(fallback.payRunId);
            return fallback;
          }
        }

        setResolveError(
          status === 404
            ? `Pay run ID "${payRunId}" was not found for company ${companyCode}.`
            : (detail ??
              `Could not resolve pay run "${payRunId}". Check the API and try again.`)
        );
        setResolvedPayRun(null);
        return null;
      } finally {
        if (generation === lookupGenerationRef.current) {
          setIsResolving(false);
        }
      }
    },
    [companyCode, mypayEnvironment, payRunIdInput]
  );

  const clear = useCallback(() => {
    setPayRunIdInput("");
    setResolvedPayRun(null);
    setResolveError(null);
  }, []);

  return {
    payRunIdInput,
    setPayRunIdInput,
    resolvedPayRun,
    validatePayRunId,
    isResolving,
    resolveError,
    clearResolved,
    clear,
  };
}
